import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  DEVICES_CREATE,
  DEVICES_UPDATE,
  DEVICES_VIEW,
} from "../../devicesPermissions";
import { archivedDevicesSearchSchema, devicesSearchSchema } from "./searchSchemas";
import type { DevicesRouteFactory } from "./devicesRouteContext";

const DevicesListScreen = lazyRouteComponent(
  () => import("../DevicesListScreen"),
  "DevicesListScreen",
);
const ArchivedDevicesListScreen = lazyRouteComponent(
  () => import("../ArchivedDevicesListScreen"),
  "ArchivedDevicesListScreen",
);
const DeviceFormScreen = lazyRouteComponent(
  () => import("../DeviceFormScreen"),
  "DeviceFormScreen",
);
const DeviceDetailScreen = lazyRouteComponent(
  () => import("../DeviceDetailScreen"),
  "DeviceDetailScreen",
);
const DeviceEditScreen = lazyRouteComponent(
  () => import("../DeviceEditScreen"),
  "DeviceEditScreen",
);

export const createDevicesListRoutes: DevicesRouteFactory = ({
  context,
  devices,
  companies,
  assets,
}) => {
  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/$id/edit`,
    beforeLoad: requirePermission([DEVICES_UPDATE]),
    component: function DeviceEditRouteScreen() {
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(DeviceEditScreen, {
        repository: devices,
        basePath: context.basePath,
        deviceId: id,
        assets,
      });
    },
  });

  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/$id`,
    beforeLoad: requirePermission([DEVICES_VIEW]),
    component: function DeviceDetailRouteScreen() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(DeviceDetailScreen, {
        repository: devices,
        basePath: context.basePath,
        deviceId: id,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: context.basePath,
      validateSearch: devicesSearchSchema,
      beforeLoad: requirePermission([DEVICES_VIEW]),
      component: () =>
        createElement(DevicesListScreen, {
          repository: devices,
          basePath: context.basePath,
          companies,
          assets,
        }),
    }),
    // 'create' and 'archived' are literal segments, registered before the
    // '$id' routes so neither is ever captured as a device id.
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/create`,
      beforeLoad: requirePermission([DEVICES_CREATE]),
      component: () =>
        createElement(DeviceFormScreen, {
          repository: devices,
          basePath: context.basePath,
          companies,
          assets,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/archived`,
      validateSearch: archivedDevicesSearchSchema,
      beforeLoad: requirePermission([DEVICES_VIEW]),
      component: () =>
        createElement(ArchivedDevicesListScreen, {
          repository: devices,
          basePath: context.basePath,
        }),
    }),
    editRoute,
    // Bare '$id' is the most general path here -- kept last.
    detailRoute,
  ];
};
